import { useState } from "react";
import BottomSheet from "./BottomSheet";
import { GROUP_COLOURS } from "../lib/constants";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

export default function CreateGroupModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [colour, setColour] = useState("teal");
  const [wildcards, setWildcards] = useState(2);
  const [seasonWeeks, setSeasonWeeks] = useState(10);
  const [seasonStart, setSeasonStart] = useState("");
  const [prize1, setPrize1] = useState("");
  const [prize2, setPrize2] = useState("");
  const [prize3, setPrize3] = useState("");
  const [showPrizes, setShowPrizes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName(""); setColour("teal"); setWildcards(2);
    setSeasonWeeks(10); setSeasonStart("");
    setPrize1(""); setPrize2(""); setPrize3(""); setShowPrizes(false);
  };
  const handleClose = () => { reset(); onClose(); };

  const submit = async () => {
    if (!name.trim()) { toast.error("Group name is required"); return; }
    setSubmitting(true);
    try {
      const parse = (v) => (v === "" || v == null ? null : Number(v));
      const { data } = await api.post("/groups", {
        name: name.trim(),
        colour,
        wildcards_per_participant: Number(wildcards),
        season_weeks: Number(seasonWeeks),
        season_start_date: seasonStart || null,
        prize_1st: parse(prize1),
        prize_2nd: parse(prize2),
        prize_3rd: parse(prize3),
        show_prizes_on_leaderboard: showPrizes,
      });
      onCreated && onCreated(data.group);
      toast.success(`"${data.group.name}" created`);
      handleClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Failed to create group"));
    } finally { setSubmitting(false); }
  };

  return (
    <BottomSheet open={open} onClose={handleClose} title="Create new group" testId="create-group-modal">
      <div className="space-y-5">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">Group name</label>
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Melbourne Cup Crew"
            className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1971c2] focus:ring-2 focus:ring-[#1971c2]/20 font-medium text-base transition-colors"
            data-testid="create-group-name-input"
          />
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">Colour</label>
          <div className="grid grid-cols-6 gap-2" data-testid="create-group-colour-grid">
            {GROUP_COLOURS.map((c) => (
              <button
                key={c.key}
                onClick={() => setColour(c.key)}
                className={`h-12 rounded-xl transition-transform active:scale-90 ${colour === c.key ? "ring-4 ring-offset-2 ring-[#1971c2]" : ""}`}
                style={{ backgroundColor: c.hex }}
                aria-label={c.label}
                data-testid={`create-group-colour-${c.key}`}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
            Wildcards per participant: <span className="text-[#1971c2] font-black">{wildcards}</span>
          </label>
          <input
            type="range" min={0} max={5} value={wildcards}
            onChange={(e) => setWildcards(Number(e.target.value))}
            className="w-full accent-[#1971c2]"
            data-testid="create-group-wildcards-slider"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
              Season length
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={1} max={20} value={seasonWeeks}
                onChange={(e) => setSeasonWeeks(Number(e.target.value))}
                className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#1971c2] font-bold text-base text-center"
                data-testid="create-group-season-weeks"
              />
              <span className="text-xs font-bold text-[#6c757d]">weeks</span>
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">
              Season start (optional)
            </label>
            <input
              type="date" value={seasonStart}
              onChange={(e) => setSeasonStart(e.target.value)}
              className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#1971c2] font-medium text-sm"
              data-testid="create-group-season-start"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d] mb-2">Prizes (optional)</label>
          <div className="grid grid-cols-3 gap-2">
            {[
              { v: prize1, s: setPrize1, l: "1st $", t: "prize-1st" },
              { v: prize2, s: setPrize2, l: "2nd $", t: "prize-2nd" },
              { v: prize3, s: setPrize3, l: "3rd $", t: "prize-3rd" },
            ].map((p) => (
              <div key={p.t}>
                <div className="text-[10px] font-bold text-[#6c757d] mb-1">{p.l}</div>
                <input
                  type="number" min="0" value={p.v}
                  onChange={(e) => p.s(e.target.value)} placeholder="0"
                  className="w-full bg-white border-2 border-[#e9ecef] rounded-xl px-3 py-2.5 focus:outline-none focus:border-[#1971c2] font-bold text-base text-center transition-colors"
                  data-testid={`create-group-${p.t}`}
                />
              </div>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-3 bg-[#f8f9fa] rounded-xl p-3 cursor-pointer">
          <input
            type="checkbox" checked={showPrizes}
            onChange={(e) => setShowPrizes(e.target.checked)}
            className="w-5 h-5 accent-[#1971c2]"
            data-testid="create-group-show-prizes-checkbox"
          />
          <div className="font-bold text-sm">Show prizes on leaderboard</div>
        </label>

        <button
          onClick={submit}
          disabled={submitting}
          className="w-full bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold text-base rounded-xl py-3.5 active:scale-95 transition-transform shadow-md disabled:opacity-60"
          data-testid="create-group-submit"
        >
          {submitting ? "Creating…" : "Create group"}
        </button>
      </div>
    </BottomSheet>
  );
}
